import React from 'react';
import {
  EmployeeDashboardGrid,
  EmployeeSummaryCard,
  EmployeeWorkspaceEmptyState,
  EmployeeWorkspaceSection
} from '../../components/workforce/EmployeeWorkspace';

const PLANNED_MESSAGE = 'This capability is planned once employee operational KPIs become available.';

function PlannedOperationalSection({ title, description }) {
  return (
    <EmployeeWorkspaceSection title={title} description="Employee-level data availability">
      <EmployeeWorkspaceEmptyState
        title={`${title} are planned`}
        description={`${description} ${PLANNED_MESSAGE}`}
        release=""
      />
    </EmployeeWorkspaceSection>
  );
}

export default function EmployeeOperationalKpisWorkspace({ profile }) {
  const employeeProfileId = Number(profile?.id);
  const hasEmployeeScope = Number.isFinite(employeeProfileId) && employeeProfileId > 0;

  return (
    <div className="employee-operational-kpis" data-testid="employee-operational-kpis-workspace">
      <EmployeeWorkspaceSection
        title="Operational summary"
        description="Employee-scoped operational reporting availability"
      >
        <EmployeeDashboardGrid columns="wide">
          <EmployeeSummaryCard label="Assigned KPIs" value="Not available" detail="No employee KPI assignment source exists" />
          <EmployeeSummaryCard label="Measured KPIs" value="Not available" detail="No employee operational actuals source exists" />
          <EmployeeSummaryCard label="Last updated" value="Not available" detail="No employee KPI reporting feed is connected" />
          <EmployeeSummaryCard
            label="Reporting scope"
            value={hasEmployeeScope ? 'Employee' : 'Unavailable'}
            detail={hasEmployeeScope ? `Employee profile #${employeeProfileId}` : 'No employee profile is selected'}
          />
        </EmployeeDashboardGrid>
        <p className="employee-workspace__scope-note" data-testid="operational-kpi-scope-disclosure">
          Existing operational KPIs are available only at location and manager scope. They are not displayed here as employee performance.
          {' '}{PLANNED_MESSAGE}
        </p>
      </EmployeeWorkspaceSection>

      <PlannedOperationalSection
        title="Current operational metrics"
        description="The current data model has no employee-level operational actuals, scorecards, NPS, ancillary sales, productivity, conversion, or utilization measures."
      />

      <PlannedOperationalSection
        title="Goals progress"
        description="Goals are currently uploaded at location scope and allocated to manager records; the repository does not support employee-owned operational goals."
      />

      <EmployeeDashboardGrid>
        <PlannedOperationalSection
          title="Operational trends"
          description="Historical KPI actuals exist for manager and location reporting, not for employee profiles."
        />
        <PlannedOperationalSection
          title="Operational comparisons"
          description="No supported employee comparison, benchmark, ranking, or leaderboard source exists."
        />
      </EmployeeDashboardGrid>

      <PlannedOperationalSection
        title="Operational notes"
        description="No operational KPI note source is linked to employee profiles. Coaching records remain permission-protected in the Coaching workspace and are not treated as KPI evidence."
      />

      <EmployeeWorkspaceSection title="Future AI insights" description="Reserved architecture — no AI processing is active">
        <EmployeeWorkspaceEmptyState
          title="AI operational insights are planned"
          description="No recommendations, coaching guidance, generated scores, or inferred employee performance are produced. Governed insights require authoritative employee operational KPIs first."
          release="Future release"
        />
      </EmployeeWorkspaceSection>
    </div>
  );
}

export { PLANNED_MESSAGE };
